import { createElement, useCallback } from 'react';

import { CssVarsProvider } from '@mui/joy/styles';
import { flushSync } from 'react-dom';
import { Root, createRoot } from 'react-dom/client';

import { track } from '../../services/analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_closed,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../../services/analytics/events';
import { driver } from '../../services/driverjs';
import { theme } from '../../theme';
import { brand } from '../../theme/brand';

import {
  BACK_BUTTON_TEXT,
  CONTINUE_BUTTON_TEXT,
  FINISH_BUTTON_TEXT,
  TOUR_THEME_CSS_ID,
} from './constants';
import { HomeOnboardingPopover } from './popover/HomeOnboardingPopover';
import { TOUR_THEME_CSS } from './style';
import { runTourAction } from './tourActions';
import type { OnboardingStep, TourEventProps } from './types';

export const hasVisibleOnboardingSteps = (steps: OnboardingStep[]): boolean =>
  steps.some((step) => step.show);

const tourHeaderText = (title?: string): string => (title ?? '').split(' ').slice(0, 3).join(' ');

const injectTourThemeStyle = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TOUR_THEME_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = TOUR_THEME_CSS_ID;
  style.textContent = TOUR_THEME_CSS;
  document.head.appendChild(style);
};

export const runOnboardingTour = (
  steps: OnboardingStep[],
  eventProps: TourEventProps,
  options: { hideBack?: boolean; popoverWidth?: number } = {}
) => {
  const visibleSteps = steps.filter((step) => step.show);
  if (visibleSteps.length === 0) return;

  injectTourThemeStyle();

  const isDarkMode = document.documentElement.getAttribute('data-joy-color-scheme') === 'dark';
  const overlayColor = isDarkMode ? brand.blur.light : brand.blur.dark;
  const overlayOpacity = isDarkMode ? 0.2 : 0.1;

  let popoverRoot: Root | null = null;
  let customContainer: HTMLDivElement | null = null;
  let interactiveTarget: HTMLElement | null = null;
  let interactiveHandler: ((event: Event) => void) | null = null;

  let lastShownIdx = -1;
  const stepEventProps = (idx: number) => {
    const step = visibleSteps[idx];
    // Per-step (number + header text) overlaid on top of the launcher-provided
    // context (platform_page, feature_name, fastpass_used, …). Order matters:
    // spread eventProps first so per-step values win for the shared keys, and
    // any extra launcher props (fastpass counts, etc.) ride along too.
    return {
      ...eventProps,
      tutorial_step_number: `Step ${idx + 1}`,
      tutorial_screen_header_text: tourHeaderText(step?.title),
    };
  };

  const detachInteractiveListener = () => {
    if (interactiveTarget && interactiveHandler) {
      interactiveTarget.removeEventListener('click', interactiveHandler);
    }
    interactiveTarget = null;
    interactiveHandler = null;
  };

  const attachInteractiveListener = (idx: number) => {
    detachInteractiveListener();
    const step = visibleSteps[idx];
    if (!step?.interactive) return;
    const target = document.querySelector(step.element);
    if (!(target instanceof HTMLElement)) return;
    const handler = () => {
      if (idx === visibleSteps.length - 1) tour.destroy();
      else navigateTo(idx + 1, 'next');
    };
    target.addEventListener('click', handler);
    interactiveTarget = target;
    interactiveHandler = handler;
  };

  // Centralised transition helper: awaits the target step's beforeActions
  // (open drawer, expand accordion, etc.) BEFORE invoking the driver.js
  // navigation method, so driver.js measures against the resting DOM.
  const navigateTo = async (idx: number, kind: 'drive' | 'next' | 'prev') => {
    const step = visibleSteps[idx];
    // Run pre-step actions FIRST (open drawer, expand accordion, etc.) so
    // the DOM is mounted when driver.js queries for the target element.
    for (const key of step?.beforeActions ?? []) {
      await runTourAction(key);
    }
    // Diagnostic: warn if the target selector doesn't match anything — driver.js
    // silently centres the popover in the viewport when the target is missing,
    // which looks like the highlight is "wrong" but is really a selector miss.
    if (step?.element && !document.querySelector(step.element)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[onboarding] step ${idx + 1} target not found in DOM: ${step.element} — driver.js will skip the highlight stage.`
      );
    }
    try {
      if (kind === 'drive') tour.drive();
      else if (kind === 'next') tour.moveNext();
      else tour.movePrevious();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[onboarding] driver.js navigation failed', { idx, kind, err });
    }
  };

  // Standalone green dot pinned to the highlight's corner facing the popover —
  // independent of driver.js's arrow (which we hide), so it's never thrown off
  // by floating-ui's overflow shifting of the popover.
  let floatingDot: HTMLDivElement | null = null;
  let floatingArrow: HTMLDivElement | null = null;
  const ensureDot = (): HTMLDivElement => {
    if (!floatingDot) {
      floatingDot = document.createElement('div');
      floatingDot.className = 'calyx-tour-dot';
      document.body.appendChild(floatingDot);
    }
    return floatingDot;
  };
  const ensureArrow = (): HTMLDivElement => {
    if (!floatingArrow) {
      floatingArrow = document.createElement('div');
      floatingArrow.className = 'calyx-tour-arrow';
      document.body.appendChild(floatingArrow);
    }
    return floatingArrow;
  };
  const removeOverlayBits = () => {
    if (floatingDot && floatingDot.parentNode) floatingDot.parentNode.removeChild(floatingDot);
    if (floatingArrow && floatingArrow.parentNode)
      floatingArrow.parentNode.removeChild(floatingArrow);
    floatingDot = null;
    floatingArrow = null;
  };
  const rectsOverlap = (a: DOMRect, b: DOMRect): boolean =>
    !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);

  const positionDotForStep = (idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return removeOverlayBits();
    const target = document.querySelector(step.element);
    if (!(target instanceof HTMLElement)) return removeOverlayBits();
    const t = target.getBoundingClientRect();
    const popover = document.querySelector<HTMLElement>('.driver-popover');
    const p = popover?.getBoundingClientRect();
    // If after driver.js's own scrollIntoView the popover still overlaps the
    // highlight (target is taller than the viewport, for example) drop the
    // visual cues — popover-on-highlight is its own indicator.
    if (p && rectsOverlap(p, t)) return removeOverlayBits();
    const dot = ensureDot();
    const arrow = ensureArrow();
    // Default placement: pick the axis the popover sits on relative to the
    // target via |dx| vs |dy| (PR #1031's original logic — works for the home
    // tour and any step where driver.js places the popover roughly on a single
    // axis from the target).
    //
    // When step.align is 'start' or 'end', we override: axis is taken from
    // step.side, and the arrow is pinned to the corresponding popover corner
    // (inset 28px) instead of being clamped to the popover/target centre. This
    // is what step 3 of the unrated tour needs (align='end' + side='top'
    // → arrow at popover bottom-right pointing down at the Done button).
    let dotX = t.left + t.width / 2;
    let dotY = t.top + t.height / 2;
    let arrowX = dotX;
    let arrowY = dotY;
    let arrowSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
    if (p) {
      const targetCx = t.left + t.width / 2;
      const targetCy = t.top + t.height / 2;
      const popoverCx = p.left + p.width / 2;
      const popoverCy = p.top + p.height / 2;
      const dx = popoverCx - targetCx;
      const dy = popoverCy - targetCy;
      const ARROW_CORNER_INSET = 28;
      const cornerAligned = step.align === 'start' || step.align === 'end';
      const verticalAxis = cornerAligned
        ? step.side === 'top' || step.side === 'bottom'
        : Math.abs(dy) >= Math.abs(dx);
      if (verticalAxis) {
        const popoverIsBelow = dy > 0;
        arrowSide = popoverIsBelow ? 'top' : 'bottom';
        arrowY = popoverIsBelow ? p.top : p.bottom;
        if (step.centerDot) {
          // Point the arrow at the (centered) dot so the two stay connected,
          // while the popover itself keeps its aligned placement.
          arrowX = Math.max(p.left + 12, Math.min(p.right - 12, targetCx));
        } else if (step.align === 'end') {
          arrowX = p.right - ARROW_CORNER_INSET;
        } else if (step.align === 'start') {
          arrowX = p.left + ARROW_CORNER_INSET;
        } else {
          arrowX = Math.max(t.left + 12, Math.min(t.right - 12, popoverCx));
        }
        dotY = popoverIsBelow ? t.bottom : t.top;
        dotX = step.centerDot
          ? targetCx
          : cornerAligned
            ? Math.max(t.left + 12, Math.min(t.right - 12, arrowX))
            : arrowX;
      } else {
        const popoverIsRight = dx > 0;
        arrowSide = popoverIsRight ? 'left' : 'right';
        arrowX = popoverIsRight ? p.left : p.right;
        if (step.centerDot) {
          // Point the arrow at the (centered) dot so the two stay connected,
          // while the popover itself keeps its aligned placement.
          arrowY = Math.max(p.top + 12, Math.min(p.bottom - 12, targetCy));
        } else if (step.align === 'end') {
          arrowY = p.bottom - ARROW_CORNER_INSET;
        } else if (step.align === 'start') {
          arrowY = p.top + ARROW_CORNER_INSET;
        } else {
          arrowY = Math.max(t.top + 12, Math.min(t.bottom - 12, popoverCy));
        }
        dotX = popoverIsRight ? t.right : t.left;
        dotY = step.centerDot
          ? targetCy
          : cornerAligned
            ? Math.max(t.top + 12, Math.min(t.bottom - 12, arrowY))
            : arrowY;
      }
    }
    dot.style.left = `${dotX}px`;
    dot.style.top = `${dotY}px`;
    arrow.style.left = `${arrowX}px`;
    arrow.style.top = `${arrowY}px`;
    arrow.dataset.side = arrowSide;
  };
  let lastIdx = 0;
  const repositionDot = () => positionDotForStep(lastIdx);
  window.addEventListener('resize', repositionDot);
  window.addEventListener('scroll', repositionDot, true);

  const mountReact = (popover: { wrapper: HTMLElement }, idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return;

    const wrapper = popover.wrapper;
    wrapper.style.padding = '0';
    wrapper.style.maxWidth = 'none';
    wrapper.style.width = 'auto';
    wrapper.style.background = 'transparent';
    wrapper.style.boxShadow = 'none';
    wrapper.style.borderRadius = '10px';
    // Hide driver.js's default popover chrome but keep the arrow — the green
    // dot is positioned independently (anchored to the highlight) so it stays
    // accurate even when floating-ui shifts the popover to fit the viewport.
    Array.from(wrapper.children).forEach((child) => {
      if (child === customContainer) return;
      const el = child as HTMLElement;
      if (el.classList.contains('driver-popover-arrow')) return;
      el.style.display = 'none';
    });

    if (customContainer && customContainer.parentNode !== wrapper) {
      wrapper.appendChild(customContainer);
    }
    if (!customContainer) {
      customContainer = document.createElement('div');
      wrapper.appendChild(customContainer);
      popoverRoot = createRoot(customContainer);
    }

    flushSync(() => {
      popoverRoot?.render(
        createElement(
          CssVarsProvider,
          { theme, defaultMode: 'light' },
          createElement(HomeOnboardingPopover, {
            title: step.title,
            body: step.body,
            customContent: step.customContent,
            stepIndex: idx,
            totalSteps: visibleSteps.length,
            interactive: step.interactive,
            hideBack: options.hideBack,
            popoverWidth: options.popoverWidth,
            onBack: () => {
              track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text: BACK_BUTTON_TEXT,
              });
              if (idx > 0) navigateTo(idx - 1, 'prev');
            },
            onContinue: () => {
              track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text:
                  idx === visibleSteps.length - 1 ? FINISH_BUTTON_TEXT : CONTINUE_BUTTON_TEXT,
              });
              if (idx === visibleSteps.length - 1) tour.destroy();
              else navigateTo(idx + 1, 'next');
            },
            onClose: () => {
              track(EVNT_tutorial_screen_closed, EVENT_TUTORIAL, stepEventProps(idx));
              tour.destroy();
            },
          })
        )
      );
    });
  };

  const hasInteractiveStep = visibleSteps.some((step) => step.interactive);

  const tour = driver({
    showProgress: false,
    showButtons: [],
    allowClose: !hasInteractiveStep,
    smoothScroll: false,
    overlayColor,
    overlayOpacity,
    popoverClass: 'driverjs-onboarding-theme',
    onDestroyed: () => {
      detachInteractiveListener();
      removeOverlayBits();
      window.removeEventListener('resize', repositionDot);
      window.removeEventListener('scroll', repositionDot, true);
      const root = popoverRoot;
      popoverRoot = null;
      customContainer = null;
      if (root) queueMicrotask(() => root.unmount());
    },
    // Fires when the current step's highlight is removed (between steps).
    // Hide the dot + arrow during the transition gap so they don't linger at
    // the old step's coordinates while the new popover renders.
    onDeselected: () => {
      detachInteractiveListener();
      removeOverlayBits();
    },
    onPopoverRender: (popover, { state }) => {
      const idx = state.activeIndex ?? 0;
      // PGV once per step (onPopoverRender may fire on re-render).
      if (idx !== lastShownIdx) {
        lastShownIdx = idx;
        track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, stepEventProps(idx));
      }
      mountReact(popover, idx);
      attachInteractiveListener(idx);
      lastIdx = idx;
      // Position the floating dot on the next frame so the popover has settled
      // its final rect (we read it to decide which edge of the highlight to
      // anchor the dot to).
      requestAnimationFrame(() => positionDotForStep(idx));
    },
    steps: visibleSteps.map((s) => ({
      element: s.element,
      popover: { side: s.side, align: s.align ?? 'center', title: '', description: '' },
    })),
  });

  navigateTo(0, 'drive');
};

export const useOnboardingTour = (
  steps: OnboardingStep[],
  eventProps: TourEventProps,
  options: { hideBack?: boolean; popoverWidth?: number } = {}
) => {
  const canShow = hasVisibleOnboardingSteps(steps);
  const start = useCallback(
    () => runOnboardingTour(steps, eventProps, options),
    [steps, eventProps, options]
  );
  return { canShow, start };
};
